import {Button, Toggle} from '@finpay-development/shared-components';
import CreateIcon from '@mui/icons-material/Create';
import DateRangeIcon from '@mui/icons-material/DateRange';
import {
    Box,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import {useFormik} from 'formik';
import _ from 'lodash';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {TableColumn} from '../../../shared/model/table-column';
import {RootState} from '../../../shared/state/root-reducer';
import {admissionsAdvisorUtils} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/utils/admission-advisor-utils';
import {
    FacilityLevelOfCareWithCrossoverDays,
    PriorCareProvider,
    SummarySelections,
    ThirdPartyPayerStatus,
    Vob,
} from '@finpay-estimation/estimation-types';
import {AppDispatch} from '../../../shared/state/store';
import {saveSelectedEstimate} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import PriorCareModal from './estimator-prior-care-modal';
import {admissionsAdvisorService} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/services/admissions-advisor-services';
import {showErrorStatus} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {
    useIsDisabledAfterConvert
} from '../../hooks/use-is-disabled-after-convert';
import {findMatchedPayorPlan} from '../../utils/payor-plan-utils';

export function EstimatorPriorCareSummary() {
    const SELF_PAY_PAYOR_ID = 97;

    const [isPriorCareModalOpen, setIsPriorCareModalOpen] = useState(false);
    const [locRowsPerPage, setLocRowsPerPage] = useState(5);
    const [locPage, setLocPage] = useState(0);
    const [initialListOfLoc, setInitialListOfLoc] =
        useState<FacilityLevelOfCareWithCrossoverDays[]>();

    const locTableHeaders: TableColumn[] = [
        {id: 'levelOfCareCode', label: 'Code', minWidth: 200},
        {id: 'levelOfCareName', label: 'Name', minWidth: 200},
        {id: 'colLocDays', label: 'Days/Sessions'},
    ];

    const state = {
        vobState: useSelector(
            (state: RootState) => state.vobContext.selectedVob
        ),
        estimate: useSelector((state: RootState) => state.estimatorContext.selectedEstimate
        ),
        allClientsMap: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient.allClientsMap
        ),
        facilityLevelsOfCareList: useSelector((state: RootState) => {
            return state?.estimatorContext.facilityLevelsOfCareList;
        }),
        rateAsPayorPlan: useSelector(
            (state: RootState) => state.estimatorContext.rateAsPayorPlan
        ),
    };

    const {vobState, estimate, allClientsMap, facilityLevelsOfCareList, rateAsPayorPlan} =
        state;

    const facilityId = vobState?.facility?.facilityId as number;
    
    const clientId = vobState?.client?.clientId;
    const clientFacilities = clientId ? allClientsMap?.get(clientId)?.clientFacilities || [] : [];
    const facilities = [clientFacilities.map(cf => ({
        ...cf,
        clientId: clientId || 0,
        facilityName: cf.facilityName || '',
    } as any))];

    const isEditMode = Boolean(
        estimate?.priorCare && estimate?.priorCare.loc
    );

    const dispatch = useDispatch<AppDispatch>();

    const isDisabledAfterConvert = useIsDisabledAfterConvert()

    const handleButtonOnClick = async () =>{
        setIsPriorCareModalOpen(true);

        const provider = estimate?.priorCare?.priorCareProvider;
        const networkSelection =
            estimate?.priorCare?.thirdPartyPayerStatus;

        const priorCareFacilityId = estimate?.priorCare?.facilityId != null ? Number(estimate.priorCare.facilityId) : undefined;
        const facilityIdForLoc =
            isEditMode && priorCareFacilityId != null && priorCareFacilityId !== facilityId
                ? priorCareFacilityId
                : facilityId;

        if (provider === PriorCareProvider.THIRDPARTY) {
            const inn =
                networkSelection === ThirdPartyPayerStatus.INNETW ? 'y' : 'n';

            const response = await admissionsAdvisorService
                .getMasterFacilityLevelsOfCare(facilityIdForLoc, inn);

            if (response.hasErrors) dispatch(showErrorStatus('Error fetching third party LOC'));

            const newLoc = response.hasErrors
                ? []
                : response.entity?.filter(
                    (loc: FacilityLevelOfCareWithCrossoverDays) => {
                        return loc.isCovered;
                    }
                );
            setInitialListOfLoc(newLoc);
        } else {
            if (facilityIdForLoc !== facilityId) {
                const responsePayers = await admissionsAdvisorService.getEstimatorFacilityLocPayers(
                    -2,
                    facilityIdForLoc
                );
                if (!responsePayers.hasErrors && responsePayers.entity?.facilityPayors) {
                    const rateAs = (vobState as any)?.rateAsPayorPlan;
                    const useRateAs = rateAs?.isOverridden === true && rateAs?.payor && rateAs?.plan;
                    const payorToUse = useRateAs ? rateAs.payor : vobState?.payor;
                    const planToUse = useRateAs ? rateAs.plan : vobState?.plan;
                    const matchedPlan = findMatchedPayorPlan(
                        responsePayers.entity.facilityPayors,
                        payorToUse,
                        planToUse,
                        [],
                        new Map()
                    );
                    if (matchedPlan) {
                        const facilityPlanId = matchedPlan.plan.facilityPlanId || 0;
                        const response = await admissionsAdvisorService.getFacilityLevelsOfCare(
                            facilityIdForLoc,
                            facilityPlanId
                        );
                        setInitialListOfLoc(response.hasErrors ? [] : (response.entity || []));
                    } else {
                        dispatch(showErrorStatus('No Levels of Care available for this Facility/Payer'));
                        setInitialListOfLoc([]);
                    }
                } else {
                    setInitialListOfLoc([]);
                }
            } else {
                setInitialListOfLoc(
                    (facilityLevelsOfCareList as FacilityLevelOfCareWithCrossoverDays[]) || []
                );
            }
        }
    }

    const handleLocChangePage = (event: unknown, newPage: number) => {
        setLocPage(newPage);
    };

    const handleChangeLocRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setLocRowsPerPage(+event.target.value);
        setLocPage(0);
    };

    const doesPriorCareExist =
        Array.isArray(estimate?.priorCare?.loc) &&
        estimate?.priorCare?.loc.length! > 0;

    const priorCareSummaryFormik = useFormik({
        initialValues: {
            priorCareYesNo: doesPriorCareExist,
        },
        onSubmit: () => {},
        enableReinitialize: true,
    });

    const handleHasPriorCareChange = async () => {
        const isAddingPriorCare = !priorCareSummaryFormik.values.priorCareYesNo;

        if (!isAddingPriorCare && estimate?.priorCare) {
            await dispatch(
                saveSelectedEstimate({
                    estimateId: estimate?.estimateId,
                    // @ts-ignore
                    estimate: {
                        ..._.omit(estimate, [
                          'priorCare',
                          'estimateId',
                          'summary',
                          'createUserId',
                          'createDt',
                          'lastUpdateUserId',
                          'lastUpdateDt',
                          'priorCareSummary',
                          'crossOverSummary',
                        ]),
                        summarySelection: SummarySelections.NONADJUSTED,
                        ...(rateAsPayorPlan && { rateAsPayorPlan }),
                      },
                })
            );
        }
    };

    const handleModalCancel = () => {
        setIsPriorCareModalOpen(false);
    };

    const handleModalSubmit = () => {
        setIsPriorCareModalOpen(false);
    };

    return (
        <>
            <Paper>
                <Box
                    style={{
                        padding: '1.0em',
                        borderBottom: '1px solid #cccccc',
                        marginBottom: '1.5em',
                    }}
                >
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                    >
                        <Grid
                            item
                            justifyContent="center"
                            direction="column"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <DateRangeIcon sx={{marginRight: '0.25em'}} />{' '}
                                Adjustments for Prior Care
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Toggle
                                leftText="Yes"
                                rightText="No"
                                name="priorCareYesNo"
                                onChange={() => {
                                    handleHasPriorCareChange();
                                }}
                                value={
                                    priorCareSummaryFormik.values.priorCareYesNo
                                }
                                test-id="prior-care-yes-no-toggle"
                                formik={priorCareSummaryFormik}
                                disabled={isDisabledAfterConvert}
                            />
                        </Grid>
                    </Grid>
                    <Grid container direction="row">
                        {(() => {
                            switch (true) {
                                case doesPriorCareExist: {
                                    const priorLevelsOfCareSummary =
                                        admissionsAdvisorUtils.getNewPriorLevelsOfCare(
                                            estimate?.priorCare!,
                                            facilities
                                        );
                                    const genericPriorLoc =
                                        admissionsAdvisorUtils.getNewGenericPriorAdjChg(
                                            estimate?.priorCare!,
                                            estimate!,
                                            vobState?.payor?.payorId === SELF_PAY_PAYOR_ID || true
                                        );
                                    return (
                                        <>
                                            <>
                                                <Grid
                                                    item
                                                    direction="column"
                                                    md={5}
                                                >
                                                    <Typography variant="body2">
                                                        {
                                                            priorLevelsOfCareSummary?.labelOne
                                                        }
                                                    </Typography>
                                                    {
                                                        priorLevelsOfCareSummary?.valueOne
                                                    }
                                                </Grid>
                                                <Grid
                                                    item
                                                    direction="column"
                                                    md={5}
                                                >
                                                    <Typography variant="body2">
                                                        {
                                                            priorLevelsOfCareSummary?.labelTwo
                                                        }
                                                    </Typography>
                                                    {
                                                        priorLevelsOfCareSummary?.valueTwo
                                                    }
                                                </Grid>
                                                <Grid
                                                    item
                                                    direction="column"
                                                    md={2}
                                                    style={{textAlign: 'right'}}
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        title="Edit Prior Care"
                                                        style={{
                                                            fontSize: '1.5rem',
                                                        }}
                                                        onClick={handleButtonOnClick}
                                                        disabled={isDisabledAfterConvert}
                                                    >
                                                        <CreateIcon /> Edit
                                                    </IconButton>
                                                </Grid>
                                                <TableContainer
                                                    component={Paper}
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {locTableHeaders.map(
                                                                    column => (
                                                                        <TableCell
                                                                            key={
                                                                                column.id
                                                                            }
                                                                        >
                                                                            {
                                                                                column.label
                                                                            }
                                                                        </TableCell>
                                                                    )
                                                                )}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {_.sortBy(
                                                                genericPriorLoc,
                                                                'name'
                                                            ).slice!(
                                                                locPage *
                                                                    locRowsPerPage,
                                                                locPage *
                                                                    locRowsPerPage +
                                                                    locRowsPerPage
                                                            ).map(
                                                                (
                                                                    row,
                                                                    index
                                                                ) => {
                                                                    return (
                                                                        <TableRow
                                                                            key={`${row.code}.${index}`}
                                                                        >
                                                                            <TableCell>
                                                                                {
                                                                                    row.code
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {
                                                                                    row.name
                                                                                }
                                                                                {estimate?.priorCare?.priorCareProvider === PriorCareProvider.CLIENT && (
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize:
                                                                                                '8pt',
                                                                                        }}
                                                                                    >
                                                                                        {admissionsAdvisorUtils.getFacilityName(
                                                                                            row.facilityId,
                                                                                            facilities
                                                                                        )}
                                                                                    </div>
                                                                                    )
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {
                                                                                    row.days
                                                                                }
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                }
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                    <Grid container>
                                                        <Grid item xs={12}>
                                                            <TablePagination
                                                                component="div"
                                                                rowsPerPageOptions={[
                                                                    5,
                                                                    10,
                                                                    50,
                                                                    {
                                                                        value: genericPriorLoc?.length!,
                                                                        label: 'All',
                                                                    },
                                                                ]}
                                                                count={
                                                                    genericPriorLoc?.length!
                                                                }
                                                                rowsPerPage={
                                                                    locRowsPerPage
                                                                }
                                                                page={locPage}
                                                                labelRowsPerPage={
                                                                    'View'
                                                                }
                                                                onPageChange={
                                                                    handleLocChangePage
                                                                }
                                                                onRowsPerPageChange={
                                                                    handleChangeLocRowsPerPage
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </TableContainer>
                                            </>
                                        </>
                                    );
                                }
                                case priorCareSummaryFormik.values
                                    .priorCareYesNo: {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            style={{
                                                textAlign: 'center',
                                                padding: '1em',
                                            }}
                                        >
                                            <Button
                                                onClick={handleButtonOnClick}
                                                test-id="enter-prior-care-button"
                                                disabled={isDisabledAfterConvert}
                                            >
                                                Enter Prior Care
                                            </Button>
                                        </Grid>
                                    );
                                }
                                default:
                                    return <></>;
                            }
                        })()}
                    </Grid>
                </Box>
            </Paper>
            {estimate &&
                estimate.estimateId &&
                isPriorCareModalOpen &&
                initialListOfLoc && (
                    <PriorCareModal
                        open={isPriorCareModalOpen}
                        isEdit={isEditMode}
                        facilities={facilities}
                        facilityId={facilityId}
                        vob={vobState as any as Vob}
                        initialListOfLoc={initialListOfLoc}
                        priorCare={estimate.priorCare}
                        handlePriorCareModalCancel={handleModalCancel}
                        handlePriorCareModalSubmit={handleModalSubmit}
                    />
                )}
        </>
    );
}
